/**
 * The Bexio workspace overview (`/admin/bexio`).
 *
 * Replaces the long read-only page that used to live here. Everything on screen
 * is a SUMMARY or a short PREVIEW; the full reconciliation tables, the
 * side-by-side and the draft preview move to their own pages in later slices
 * and are simply not rendered here any more.
 *
 * REQUEST BUDGET — the reason this is a new page rather than a rearranged one.
 * The old overview auto-paged through every mirrored invoice, up to 40 requests,
 * to fill a table nobody reads on a dashboard, and additionally fetched the
 * side-by-side and the draft preview. This one makes FIVE requests, all of them
 * bounded:
 *
 *   GET /status                        counts + connection + last successful sync
 *   GET /reconciliation                payment summary + mapping coverage + warnings
 *   GET /invoices?limit=5              the preview rows (+ `total` for the footer)
 *   GET /contacts?limit=5              the preview rows (+ `total`)
 *   GET /mapping/contacts/status?…     one batched call for those five contacts
 *   GET /sync-runs?limit=5             the run preview
 *
 * (Six calls, five endpoints — the mapping batch only fires when contacts came
 * back.) Each section fetches independently so one failing endpoint degrades its
 * own card instead of blanking the page.
 *
 * NOT SHOWN, and deliberately: the design reference's bottom status strip
 * (tenant, environment, timezone) and its per-run duration / trigger / retry /
 * API-call figures. None of that exists in the API or the mirror tables, and a
 * dashboard that invents its own operational facts is worse than one that omits
 * them.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText,
  Link2,
  RefreshCw,
  Timer,
  Users,
  Wallet,
} from "lucide-react";

import {
  fetchBexioContacts,
  fetchBexioInvoices,
  fetchBexioReconciliation,
  fetchBexioStatus,
  fetchBexioSyncRuns,
  getBexioContactMappingStatuses,
} from "../../../api/adminData/bexio";
import { getTokenPayload } from "../../../authStore";
import { Skeleton } from "../../../components/ui/skeleton";
import { useBexioSync } from "../../../components/admin/bexio/useBexioSync";
import { formatDateTime } from "../../../utils/datetime";
import {
  CONNECTION_STATUS_LABEL,
  connectionTone,
  mapLabel,
  relativeFromNow,
} from "../../../components/admin/bexio/bexioFormat";
import BexioContactsPreview from "../../../components/admin/bexio/overview/BexioContactsPreview";
import BexioInvoicesPreview from "../../../components/admin/bexio/overview/BexioInvoicesPreview";
import BexioKpiCard from "../../../components/admin/bexio/overview/BexioKpiCard";
import BexioMappingCoverageCard from "../../../components/admin/bexio/overview/BexioMappingCoverageCard";
import BexioPaymentSummaryCard from "../../../components/admin/bexio/overview/BexioPaymentSummaryCard";
import BexioSyncPreview from "../../../components/admin/bexio/overview/BexioSyncPreview";
import BexioWarningsCard from "../../../components/admin/bexio/overview/BexioWarningsCard";

/** How many rows each preview shows. Sent to the API, not sliced client-side. */
const PREVIEW_LIMIT = 5;
/** How many more each "Mehr laden" asks for. */
const PREVIEW_STEP = 10;
/**
 * Where "Mehr laden" stops.
 *
 * The point of expanding in place is to answer a question without leaving the
 * dashboard, not to reproduce the invoice list here. Past this the footer keeps
 * stating "50 von 213" and simply stops offering more, which is the truth rather
 * than a button that no longer does anything.
 */
const PREVIEW_MAX = 50;

const CONNECTION_PILL = {
  good:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  warn:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  bad:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  neutral: "border-border bg-muted text-muted-foreground",
};

function isAdmin() {
  try {
    return getTokenPayload()?.role === "admin";
  } catch {
    return false;
  }
}

function ErrorCard({ message, onRetry }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
      <p>{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-lg border border-red-300 px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-red-500/40"
        >
          Erneut versuchen
        </button>
      ) : null}
    </div>
  );
}

function CardSkeleton({ className = "h-48" }) {
  return (
    <div className={`rounded-xl border border-border bg-card p-5 ${className}`}>
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-4 h-3 w-full" />
      <Skeleton className="mt-2 h-3 w-5/6" />
      <Skeleton className="mt-2 h-3 w-2/3" />
    </div>
  );
}

export default function BexioOverviewPage() {
  const admin = isAdmin();

  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [statusLoading, setStatusLoading] = useState(true);

  const [recon, setRecon] = useState(null);
  const [reconError, setReconError] = useState("");
  const [reconLoading, setReconLoading] = useState(true);

  const [invoices, setInvoices] = useState(null);
  const [invoiceTotal, setInvoiceTotal] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesLoadingMore, setInvoicesLoadingMore] = useState(false);

  const [contacts, setContacts] = useState(null);
  const [contactTotal, setContactTotal] = useState(null);
  const [contactStatuses, setContactStatuses] = useState({});
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsLoadingMore, setContactsLoadingMore] = useState(false);

  const [runs, setRuns] = useState(null);
  const [runTotal, setRunTotal] = useState(null);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsLoadingMore, setRunsLoadingMore] = useState(false);

  /**
   * How many rows each preview is currently asking for.
   *
   * A ref, not state, and deliberately: the loaders below read it, so as state it
   * would be a dependency of every loader and therefore of `loadAll`, and the
   * effect that calls `loadAll` would re-run all six requests every time one card
   * expanded. The value only ever needs to be READ during a fetch, never
   * rendered, which is exactly what a ref is for.
   */
  const limitsRef = useRef({
    invoices: PREVIEW_LIMIT,
    contacts: PREVIEW_LIMIT,
    runs: PREVIEW_LIMIT,
  });

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError("");
    try {
      setStatus(await fetchBexioStatus());
    } catch (e) {
      setStatus(null);
      setStatusError(e?.message || "Bexio-Status konnte nicht geladen werden.");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadRecon = useCallback(async () => {
    setReconLoading(true);
    setReconError("");
    try {
      setRecon(await fetchBexioReconciliation());
    } catch (e) {
      setRecon(null);
      setReconError(e?.message || "Zahlungsabgleich konnte nicht geladen werden.");
    } finally {
      setReconLoading(false);
    }
  }, []);

  // `silent` keeps an expand from swapping the card out for a skeleton: the rows
  // already on screen stay put and the footer button carries the pending state.
  const loadInvoices = useCallback(async ({ limit, silent = false } = {}) => {
    const next = limit ?? limitsRef.current.invoices;
    limitsRef.current.invoices = next;
    if (!silent) setInvoicesLoading(true);
    try {
      const res = await fetchBexioInvoices({ limit: next, offset: 0 });
      setInvoices(Array.isArray(res?.items) ? res.items : []);
      setInvoiceTotal(typeof res?.total === "number" ? res.total : null);
    } catch {
      // A failed preview must not take the dashboard down with it.
      setInvoices([]);
      setInvoiceTotal(null);
    } finally {
      if (!silent) setInvoicesLoading(false);
    }
  }, []);

  const loadContacts = useCallback(async ({ limit, silent = false } = {}) => {
    const next = limit ?? limitsRef.current.contacts;
    limitsRef.current.contacts = next;
    if (!silent) setContactsLoading(true);
    try {
      const res = await fetchBexioContacts({ limit: next, offset: 0 });
      const items = Array.isArray(res?.items) ? res.items : [];
      setContacts(items);
      setContactTotal(typeof res?.total === "number" ? res.total : null);

      const ids = items.map((c) => c.bexio_contact_id).filter((v) => v != null);
      if (ids.length) {
        try {
          const st = await getBexioContactMappingStatuses(ids);
          const map = {};
          (st?.items || []).forEach((it) => {
            map[it.bexio_contact_id] = it;
          });
          setContactStatuses(map);
        } catch {
          // Badges are optional context; the rows still stand without them.
          setContactStatuses({});
        }
      } else {
        setContactStatuses({});
      }
    } catch {
      setContacts([]);
      setContactTotal(null);
      setContactStatuses({});
    } finally {
      if (!silent) setContactsLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async ({ limit, silent = false } = {}) => {
    const next = limit ?? limitsRef.current.runs;
    limitsRef.current.runs = next;
    if (!silent) setRunsLoading(true);
    try {
      const res = await fetchBexioSyncRuns({ limit: next, offset: 0 });
      setRuns(Array.isArray(res?.items) ? res.items : []);
      setRunTotal(typeof res?.total === "number" ? res.total : null);
    } catch {
      setRuns([]);
      setRunTotal(null);
    } finally {
      if (!silent) setRunsLoading(false);
    }
  }, []);

  /**
   * One expand handler shape for all three cards.
   *
   * `limitsRef` is bumped by the loader itself, so a second click while the first
   * is still in flight cannot skip a step — and the button is disabled meanwhile
   * anyway.
   */
  const makeLoadMore = (key, load, setPending) => async () => {
    const next = Math.min(limitsRef.current[key] + PREVIEW_STEP, PREVIEW_MAX);
    if (next <= limitsRef.current[key]) return;
    setPending(true);
    try {
      await load({ limit: next, silent: true });
    } finally {
      setPending(false);
    }
  };

  const atMax = (key) => limitsRef.current[key] >= PREVIEW_MAX;

  const loadAll = useCallback(() => {
    loadStatus();
    loadRecon();
    loadInvoices();
    loadContacts();
    loadRuns();
  }, [loadStatus, loadRecon, loadInvoices, loadContacts, loadRuns]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // One shared sync path for every Bexio surface — see `useBexioSync`.
  const { syncing, sync: handleSync } = useBexioSync(loadAll);

  const connection = status?.connection;
  const tone = connection?.configured ? connectionTone(connection.status) : "warn";
  const lastSync = status?.last_successful_sync;
  const relative = relativeFromNow(lastSync?.finished_at);

  return (
    <div className="p-4 sm:p-6">
      {/* The sync action lives on the page rather than in the shared workspace
          header: it owns `syncing` and refetches this page's data, and moving it
          into BexioLayout would mean lifting that state across the Outlet. */}
      <div className="mb-4 flex justify-end">
        {admin ? (
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              aria-hidden="true"
              className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`}
            />
            {syncing ? "Synchronisiert …" : "Jetzt synchronisieren"}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">
            Nur Admins können synchronisieren.
          </span>
        )}
      </div>

      {statusError ? (
        <div className="mb-4">
          <ErrorCard message={statusError} onRetry={loadStatus} />
        </div>
      ) : null}

      {/* KPI row */}
      <section aria-label="Kennzahlen" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {statusLoading ? (
          [0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-4">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="mt-3 h-3 w-20" />
              <Skeleton className="mt-2 h-5 w-16" />
            </div>
          ))
        ) : (
          <>
            <BexioKpiCard
              icon={Link2}
              tone={tone === "good" ? "good" : tone === "bad" ? "bad" : "warn"}
              label="Verbindung"
              value={
                <span
                  className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                    CONNECTION_PILL[tone] || CONNECTION_PILL.neutral
                  }`}
                >
                  {connection?.configured
                    ? mapLabel(CONNECTION_STATUS_LABEL, connection.status)
                    : "Nicht konfiguriert"}
                </span>
              }
              hint={
                connection?.last_error_category
                  ? `Letzter Fehler: ${connection.last_error_category}`
                  : undefined
              }
            />
            <BexioKpiCard
              icon={Users}
              tone="primary"
              label="Kontakte"
              value={status?.counts?.contacts ?? "—"}
              hint="In Vantio gespiegelt"
            />
            <BexioKpiCard
              icon={FileText}
              tone="primary"
              label="Rechnungen"
              value={status?.counts?.invoices ?? "—"}
              hint="Gesamt"
            />
            <BexioKpiCard
              icon={Wallet}
              tone="primary"
              label="Zahlungen"
              value={status?.counts?.payments ?? "—"}
              hint="Gesamt"
            />
            <BexioKpiCard
              icon={Timer}
              tone="neutral"
              label="Letzter erfolgreicher Sync"
              value={
                <span className="text-base font-semibold">
                  {lastSync ? formatDateTime(lastSync.finished_at) : "—"}
                </span>
              }
              hint={relative || undefined}
            />
          </>
        )}
      </section>

      {/* Reconciliation summary / mapping coverage / open notices */}
      <section
        aria-label="Abgleich und Hinweise"
        className="mt-4 grid gap-4 lg:grid-cols-[7fr_7fr_6fr]"
      >
        {reconLoading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : reconError ? (
          <div className="lg:col-span-3">
            <ErrorCard message={reconError} onRetry={loadRecon} />
          </div>
        ) : (
          <>
            <BexioPaymentSummaryCard reconciliation={recon} />
            <BexioMappingCoverageCard
              coverage={recon?.mapping_coverage}
              currency={recon?.summary?.currency}
            />
            <BexioWarningsCard reconciliation={recon} />
          </>
        )}
      </section>

      {/* Previews */}
      <section aria-label="Vorschauen" className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {invoicesLoading ? <CardSkeleton className="h-64" /> : (
          <BexioInvoicesPreview
            invoices={invoices}
            total={invoiceTotal}
            onLoadMore={
              atMax("invoices")
                ? undefined
                : makeLoadMore("invoices", loadInvoices, setInvoicesLoadingMore)
            }
            loadingMore={invoicesLoadingMore}
          />
        )}
        {contactsLoading ? <CardSkeleton className="h-64" /> : (
          <BexioContactsPreview
            contacts={contacts}
            mappingStatuses={contactStatuses}
            total={contactTotal}
            onLoadMore={
              atMax("contacts")
                ? undefined
                : makeLoadMore("contacts", loadContacts, setContactsLoadingMore)
            }
            loadingMore={contactsLoadingMore}
          />
        )}
        {runsLoading ? <CardSkeleton className="h-64" /> : (
          <BexioSyncPreview
            runs={runs}
            total={runTotal}
            onLoadMore={
              atMax("runs") ? undefined : makeLoadMore("runs", loadRuns, setRunsLoadingMore)
            }
            loadingMore={runsLoadingMore}
          />
        )}
      </section>
    </div>
  );
}
